import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { SERVICES_CONSTANTS } from '../utils/constants';
import { StringUtils } from '../utils/stringUtils';
import { ApiResponse } from '../model/auth/api-response';
import { Coupon, CouponPage, CouponRequest } from '../model/coupon-dto';

/**
 * Servicio de gestión de cupones del administrador.
 * Todos los endpoints van bajo /admin/management/coupons y requieren
 * el token de administrador (lo agrega el interceptor).
 *
 * CONVENCIÓN DE SIGNO: el backend maneja `amount`/`maxDiscountValue` en
 * NEGATIVO (misma convención que transversal.discount_rule: -50 = 50% si
 * el descuento es porcentual, o -20000 pesos si es fijo). En la UI el
 * administrador siempre ve/edita el valor POSITIVO. La conversión de signo
 * se hace únicamente en este servicio:
 *  - getCoupons(): niega lo que llega del backend para mostrarlo positivo.
 *  - createCoupon()/updateCoupon(): niega lo que envía el formulario.
 */
@Injectable({
  providedIn: 'root'
})
export class CouponService {

  private baseUrl: string = environment.baseUrl;

  constructor(
    private http: HttpClient,
    private stringUtils: StringUtils
  ) {}

  private couponsUrl(...paths: string[]): string {
    return this.stringUtils.buildUrl(
      this.baseUrl,
      SERVICES_CONSTANTS.ADMIN_PATH.MAIN,
      SERVICES_CONSTANTS.ADMIN_PATH.COUPONS.MAIN,
      ...paths
    );
  }

  getCoupons(page: number = 0, size: number = 10): Observable<ApiResponse<CouponPage>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<ApiResponse<CouponPage>>(this.couponsUrl(), { params }).pipe(
      map(response => {
        if (response.data?.list) {
          response.data.list = response.data.list.map(coupon => this.toPositiveAmount(coupon));
        }
        return response;
      })
    );
  }

  createCoupon(body: CouponRequest): Observable<ApiResponse<Coupon>> {
    return this.http.post<ApiResponse<Coupon>>(this.couponsUrl(), this.toNegativeAmount(body));
  }

  updateCoupon(id: string, body: CouponRequest): Observable<ApiResponse<Coupon>> {
    return this.http.put<ApiResponse<Coupon>>(this.couponsUrl('/', id), this.toNegativeAmount(body));
  }

  updateCouponStatus(id: string, isActive: boolean): Observable<ApiResponse<Coupon>> {
    return this.http.patch<ApiResponse<Coupon>>(
      this.couponsUrl('/', id, SERVICES_CONSTANTS.ADMIN_PATH.COUPONS.STATUS),
      { isActive }
    );
  }

  /**
   * Convierte amount/maxDiscountValue de negativo (backend) a positivo (UI).
   * `applyAutomatically` viaja tal cual (boolean plano, no aplica la
   * convención de negar el signo) gracias al spread `...coupon`.
   */
  private toPositiveAmount(coupon: Coupon): Coupon {
    return {
      ...coupon,
      amount: Math.abs(coupon.amount),
      maxDiscountValue: coupon.maxDiscountValue != null ? Math.abs(coupon.maxDiscountValue) : coupon.maxDiscountValue
    };
  }

  /**
   * Convierte amount/maxDiscountValue de positivo (UI) a negativo (backend).
   * `applyAutomatically` viaja tal cual (boolean plano) gracias al spread
   * `...body`.
   */
  private toNegativeAmount(body: CouponRequest): CouponRequest {
    return {
      ...body,
      amount: -Math.abs(body.amount),
      maxDiscountValue: body.maxDiscountValue != null ? -Math.abs(body.maxDiscountValue) : body.maxDiscountValue
    };
  }
}
